import { AxiosError, AxiosResponse } from "axios";
import { useCallback, useState } from "react";
import { useMutation } from "react-query";
import { UploadProgress } from "src/types/upload";
import { fileUpload, fileUploadOcr } from "src/utils/api/fileupload";

export type UploadedFile = { key: string; fileUrl: string };

type UseFileUploadOptions = {
  /**
   * Send the file through the OCR document check instead of straight to S3.
   * OCR uploads are proxied by the API, so they report no byte progress.
   */
  ocr?: boolean;
  onSuccess?: (
    file: UploadedFile,
    response: AxiosResponse,
    source?: File
  ) => void;
  onError?: (message: string) => void;
};

const UPLOAD_FAILED_MESSAGE =
  "Upload failed. Please check your connection and try again.";

/** Both endpoints use the standard envelope but nest the file differently. */
const readUploadedFile = (response: AxiosResponse): UploadedFile => {
  const data = response?.data?.data;
  const file = data?.uploadedFile ?? data;

  return { key: file?.key, fileUrl: file?.fileUrl };
};

/**
 * Uploads a file and reports how far along it is.
 *
 * Large files go up in resumable parts, so an upload interrupted by a flaky
 * connection continues from where it stopped rather than starting again. Pair
 * `progress` with the UploadProgress component to show size, speed and time
 * remaining.
 *
 * ```tsx
 * const { upload, progress, isUploading } = useFileUpload({
 *   onSuccess: (file) => setValue("documentKey", file.key),
 *   onError: (message) => setError("documentKey", { message }),
 * });
 * ```
 */
const useFileUpload = ({
  ocr = false,
  onSuccess,
  onError,
}: UseFileUploadOptions = {}) => {
  const [progress, setProgress] = useState<UploadProgress | null>(null);

  const { isLoading, mutateAsync, reset } = useMutation(
    ocr ? "fileUploadOcr" : "fileUpload",
    (data: FormData) =>
      ocr
        ? fileUploadOcr(data as any)
        : fileUpload(data, { onProgress: setProgress }),
    {
      onMutate: () => setProgress(null),
      onSettled: () => setProgress(null),
      onSuccess: (response: AxiosResponse, uploaded: FormData) => {
        const source = uploaded.get(ocr ? "image" : "file");

        !!onSuccess &&
          onSuccess(
            readUploadedFile(response),
            response,
            source instanceof File ? source : undefined
          );
      },
      onError: (error: AxiosError<any>) => {
        // A dropped connection has no response to read a message from.
        const message = error?.response?.data?.message;

        !!onError &&
          onError(
            typeof message === "string" ? message : UPLOAD_FAILED_MESSAGE
          );
      },
    }
  );

  /** Rejects on failure — `onError` has already reported the reason. */
  const upload = useCallback(
    (file: File, extraFields: Record<string, any> = {}) => {
      const formData = new FormData();
      formData.append(ocr ? "image" : "file", file);

      Object.entries(extraFields).forEach(([field, value]) =>
        formData.append(field, value)
      );

      return mutateAsync(formData);
    },
    [mutateAsync, ocr]
  );

  return { upload, progress, isUploading: isLoading, reset };
};

export default useFileUpload;
